import numeral from 'numeral';
import {
  Badge,
  Button,
  Icon,
  Tooltip,
  Tabs,
  Clickable,
} from '@fashionnovateam/nova-ui-toolkit';
import React from 'react';
import StatusCircle from '../ReusableComponents/StatusCircle/StatusCircle';
import { UpdatedField } from './Utils/cleanPurchaseOrderLine';
import { formatPrice } from './Utils/formatPrice';
import { DiffHighlight } from '../ReusableComponents/NewDesign/DiffHighlight/DiffHighlight';
import { FeatureFlagsContext } from '../../Lib/Contexts/FeatureFlagsContext';

export type CellProps = {
  value?: string;
  revisionValue?: UpdatedField<string>;
};

export function SizeCell({
  data,
  variant = 'primary',
}: {
  data?: UpdatedField<string>;
  variant?: 'primary' | 'secondary';
}) {
  if (!data) return <span>-</span>;
  if (data.old_value || data.new_value) {
    if (variant === 'primary') {
      return (
        <DiffHighlight newValue={data.new_value} oldValue={data.old_value} />
      );
    }
    return (
      <>
        <Badge contrast="low" color="neutral">
          {data.new_value}
        </Badge>
        <span className="overflow-hidden text-ellipsis whitespace-nowrap text-base-disabled opacity-55 line-through">
          {data.old_value}
        </span>
      </>
    );
  }
  if (data.currentValue) {
    return (
      <Badge contrast="low" color="neutral">
        {data.currentValue}
      </Badge>
    );
  }
  return <span>-</span>;
}

export function StringCell({
  data,
  variant = 'primary',
}: {
  data?: UpdatedField<string>;
  variant?: 'primary' | 'secondary';
}) {
  if (!data) return <span>-</span>;
  if (data.old_value || data.new_value) {
    return (
      <DiffHighlight
        newValue={data.new_value}
        oldValue={data.old_value}
        variant={variant}
      />
    );
  }
  if (data.currentValue) {
    return <span>{data.currentValue}</span>;
  }
  return <span>-</span>;
}

export function NumberCell({
  data,
  format = '0,0',
  variant = 'primary',
}: {
  data?: UpdatedField<number>;
  format?: string;
  variant?: 'primary' | 'secondary';
}) {
  if (!data) return <span>-</span>;
  if (data.old_value != null || data.new_value != null) {
    return (
      <DiffHighlight
        newValue={
          data.new_value != null
            ? `${numeral(data.new_value).format(format)}`
            : null
        }
        oldValue={
          data.old_value != null
            ? `${numeral(data.old_value).format(format)}`
            : null
        }
        rightAlign
        variant={variant}
      />
    );
  }
  if (data.currentValue != null) {
    return <span>{`${numeral(data.currentValue).format(format)}`}</span>;
  }
  return <span>-</span>;
}

export function UnitCostCell({
  data,
  isChargeback = false,
  isSubtotal = false,
  variant = 'primary',
}: {
  data?: UpdatedField<number>;
  isChargeback?: boolean;
  isSubtotal?: boolean;
  variant?: 'primary' | 'secondary';
}) {
  if (!data) return <span>-</span>;
  if (data.old_value || data.new_value) {
    return (
      <DiffHighlight
        newValue={
          data.new_value
            ? formatPrice(data.new_value, isChargeback, isSubtotal)
            : null
        }
        oldValue={
          data.old_value
            ? formatPrice(data.old_value, isChargeback, isSubtotal)
            : null
        }
        rightAlign
        variant={variant}
      />
    );
  }
  if (data.currentValue) {
    return (
      <span>{formatPrice(data.currentValue, isChargeback, isSubtotal)}</span>
    );
  }
  return <span>-</span>;
}

export function PrintButtonCell({
  printFn,
  isChecked = false,
  printIcon = 'tag',
  prop65 = false,
}: {
  printFn: () => void;
  isChecked: boolean;
  printIcon?: 'tag' | 'bag/paper-bag' | 'package/package-02';
  prop65?: boolean;
}) {
  const { flags } = React.useContext(FeatureFlagsContext);
  const getIcon = () => {
    switch (printIcon) {
      case 'bag/paper-bag':
        return <Icon.PaperBag size="16px" />;
      case 'package/package-02':
        return <Icon.Package02 size="16px" />;
      case 'tag':
      default:
        return <Icon.Tag size="16px" />;
    }
  };

  if (prop65 && flags?.prop65 && flags.prop65?.enabled) {
    return (
      <Tooltip
        content="You must ‘Set’ Prop 65 before printing items on this PO."
        position="top"
        arrow
      >
        <Button
          className="whitespace-nowrap"
          color="secondary"
          size="sm"
          onClick={printFn}
        >
          {getIcon()}
          <StatusCircle isChecked={isChecked} />
        </Button>
      </Tooltip>
    );
  }

  return (
    <Button
      className="whitespace-nowrap"
      color="secondary"
      size="sm"
      onClick={printFn}
    >
      {getIcon()}
      <StatusCircle isChecked={isChecked} />
    </Button>
  );
}

export function LinkCell({
  data,
}: {
  data?: {
    title: string;
    onClick: () => void;
  };
}) {
  if (!data) return <span>-</span>;
  return (
    <button
      type="button"
      onClick={data.onClick}
      className="text-[#5247DF] bg-transparent border-0 cursor-pointer p-0"
    >
      {data.title}
    </button>
  );
}

export function BooleanCell({ data }: { data?: boolean | null }) {
  if (data === null || data === undefined) return <span>-</span>;
  return <span>{data ? 'Yes' : 'No'}</span>;
}

type TabCellProps = {
  value: 'yes' | 'no' | null;
  onChange: (value: 'yes' | 'no') => void;
  error?: boolean;
};

export function TabCell({ value, onChange, error }: TabCellProps) {
  const handleClick = (newValue: 'yes' | 'no') => {
    if (value === newValue) return;
    onChange(newValue);
  };

  return (
    <div className="flex items-center gap-2">
      <Tabs.Group
        type="segment"
        className={error ? 'border border-error-600 rounded' : ''}
      >
        <Clickable onClick={() => handleClick('yes')}>
          <Tabs.Item
            icon="information/information-triangle"
            type="segment"
            isActive={value === 'yes'}
            iconColor={value === 'yes' ? '#C97118' : '#6B7280'}
          >
            Yes
          </Tabs.Item>
        </Clickable>
        <Clickable onClick={() => handleClick('no')}>
          <Tabs.Item
            icon="check/check-tick-circle"
            type="segment"
            isActive={value === 'no'}
            iconColor={value === 'no' ? '#08875D' : '#6B7280'}
          >
            No
          </Tabs.Item>
        </Clickable>
      </Tabs.Group>
      {error && (
        <div className="flex items-center">
          <Icon
            name="alert/alert-circle"
            className="nu-text-danger-700"
            size="16px"
          />
        </div>
      )}
    </div>
  );
}
